import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Lock, CheckCircle, AlertCircle, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { BACKEND_URL } from '../config/api';

const API = `${BACKEND_URL}/api`;

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: password
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password. The link may have expired.');
    }
    setLoading(false);
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-zinc-950 via-black to-zinc-950 flex items-center justify-center p-4">
        <div className="w-full max-w-md bg-zinc-900/80 border border-zinc-800 rounded-2xl p-8 text-center">
          <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
          <h1 className="text-lg font-bold text-white mb-2">Invalid Reset Link</h1>
          <p className="text-sm text-zinc-400 mb-6">This password reset link is invalid or has expired.</p>
          <Link to="/forgot-password" className="text-sm text-[#02b5ff] hover:underline">
            Request a new reset link
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-950 via-black to-zinc-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-zinc-900/80 border border-zinc-800 rounded-2xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-[#02b5ff]/10 to-[#ff025f]/10 p-8 text-center border-b border-zinc-800">
            <div className="w-14 h-14 rounded-xl bg-[#02b5ff]/10 flex items-center justify-center mx-auto mb-4">
              <Lock className="h-7 w-7 text-[#02b5ff]" />
            </div>
            <h1 className="text-xl font-bold text-white">
              {success ? 'Password Reset!' : 'Set New Password'}
            </h1>
          </div>

          <div className="p-8">
            {success ? (
              <div className="text-center space-y-4" data-testid="password-reset-success">
                <div className="w-16 h-16 rounded-full bg-green-500/10 border border-green-500/30 flex items-center justify-center mx-auto">
                  <CheckCircle className="h-8 w-8 text-green-400" />
                </div>
                <p className="text-sm text-zinc-300">Your password has been reset successfully.</p>
                <Link to="/login">
                  <Button className="w-full bg-[#02b5ff] hover:bg-[#02b5ff]/80 text-white h-11 mt-4">
                    Sign In
                  </Button>
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                {error && (
                  <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <AlertCircle className="h-4 w-4 text-red-400 flex-shrink-0" />
                    <p className="text-xs text-red-400">{error}</p>
                  </div>
                )}
                <div>
                  <label className="text-xs text-zinc-400 mb-1.5 block font-medium">New Password</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white text-sm focus:border-[#02b5ff] focus:ring-1 focus:ring-[#02b5ff] outline-none transition-colors"
                    placeholder="Minimum 6 characters"
                    required
                    minLength={6}
                    data-testid="new-password-input"
                  />
                </div>
                <div>
                  <label className="text-xs text-zinc-400 mb-1.5 block font-medium">Confirm Password</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white text-sm focus:border-[#02b5ff] focus:ring-1 focus:ring-[#02b5ff] outline-none transition-colors"
                    placeholder="Re-enter password"
                    required
                    data-testid="confirm-password-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading || !password || !confirmPassword}
                  className="w-full bg-[#02b5ff] hover:bg-[#02b5ff]/80 text-white h-11"
                  data-testid="reset-password-btn"
                >
                  {loading ? 'Resetting...' : 'Reset Password'}
                </Button>
                <Link 
                  to="/login" 
                  className="flex items-center justify-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors"
                >
                  <ArrowLeft className="h-4 w-4" /> Back to login
                </Link>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
